import {
    Stack,
    Pivot,
    PivotItem,
    Rating,
    RatingSize,
    IRatingStyleProps,
    IRatingStyles,
    IStackTokens,
    FontWeights,
    FontSizes,
} from '@fluentui/react'
import { TextField } from '@fluentui/react/lib/TextField'
import { PrimaryButton } from '@fluentui/react/lib/Button'
import { DismissFilled } from '@fluentui/react-icons'

import styles from './AnalysisPanel.module.css'

import {
    ChatAppResponse,
    getHeaders,
    parseCitation,
    ragFeedbackApi,
} from '../../api'
import { AnalysisPanelTabs } from './AnalysisPanelTabs'
import { MarkdownViewer } from '../MarkdownViewer'
import { useState, useEffect, useCallback, useRef } from 'react'
import DOMPurify from 'dompurify'

const CONTRIBUTION_CARD_PATTERN = /^\/api\/v1\/contributions\/[^/]+\/card$/

declare global {
    interface Window {
        pdfjsLib: any
    }
}

type ContributionCard = {
    id: string
    content: string
    content_snippet: string
    quality_level: string | null
    quality_score: number | null
    tags: string[]
    categories: string[]
    question_text: string | null
    published_at: string | null
    is_anonymous: boolean
    contributor: {
        handle: string | null
        first_name: string | null
        last_name: string | null
        has_picture: boolean
        picture_url: string | null
    } | null
}

type CitationContent =
    | { kind: 'empty' }
    | { kind: 'card'; card: ContributionCard }
    | { kind: 'blob'; url: string }

interface PDFViewerProps {
    src: string
}

interface Props {
    className: string
    activeTab: AnalysisPanelTabs
    onActiveTabChanged: (tab: AnalysisPanelTabs) => void
    activeCitation: string | undefined
    citationHeight: string
    answer: ChatAppResponse
    onToggleTab: (tab: AnalysisPanelTabs, index: number) => void
    selectedAnswer: number
}

const pivotItemDisabledStyle = { disabled: true, style: { color: 'grey' } }

const PDFViewer: React.FC<PDFViewerProps> = ({ src }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [pdfDoc, setPdfDoc] = useState<any>(null)

    const loadPDFJS = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (window.pdfjsLib) {
                resolve(window.pdfjsLib)
                return
            }

            const script = document.createElement('script')
            script.src =
                'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
            script.onload = () => {
                window.pdfjsLib.GlobalWorkerOptions.workerSrc =
                    'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'
                resolve(window.pdfjsLib)
            }
            script.onerror = reject
            document.head.appendChild(script)
        })
    }, [])

    const renderPage = useCallback(
        async (pageNum: number) => {
            if (!pdfDoc || !canvasRef.current) return

            try {
                const page = await pdfDoc.getPage(pageNum)
                const canvas = canvasRef.current
                const context = canvas.getContext('2d')

                // Calculate scale based on container width and mobile settings
                const containerWidth = containerRef.current?.clientWidth || 800
                const viewport = page.getViewport({ scale: 1 })
                const scale = Math.min(containerWidth / viewport.width)

                const scaledViewport = page.getViewport({ scale })

                canvas.height = scaledViewport.height
                canvas.width = scaledViewport.width

                const renderContext = {
                    canvasContext: context,
                    viewport: scaledViewport,
                }

                await page.render(renderContext).promise
                setIsLoading(false)
            } catch (err) {
                console.error('Error rendering page:', err)
                setError('Error rendering PDF page')
                setIsLoading(false)
            }
        },
        [pdfDoc]
    )

    const loadPDF = useCallback(async () => {
        if (!src) return

        try {
            setIsLoading(true)
            setError(null)

            const pdfjsLib = (await loadPDFJS()) as any
            const pdf = await pdfjsLib.getDocument(src).promise

            setPdfDoc(pdf)
            setTotalPages(pdf.numPages)

            // Extract page number from hash if present (e.g., #page=3)
            let initialPage = 1
            if (src.includes('#page=')) {
                const pageMatch = src.match(/#page=(\d+)/)
                if (pageMatch && pageMatch[1]) {
                    const pageNum = parseInt(pageMatch[1], 10)
                    if (pageNum > 0 && pageNum <= pdf.numPages) {
                        initialPage = pageNum
                    }
                }
            }
            setCurrentPage(initialPage)
        } catch (err) {
            console.error('Error loading PDF:', err)
            setError('Failed to load PDF')
            setIsLoading(false)
        }
    }, [src, loadPDFJS])

    useEffect(() => {
        loadPDF()
    }, [loadPDF])

    useEffect(() => {
        if (pdfDoc) {
            renderPage(currentPage)
        }
    }, [pdfDoc, currentPage, renderPage])

    // Cleanup PDF document on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            if (pdfDoc) {
                pdfDoc.destroy()
            }
        }
    }, [pdfDoc])

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1)
        }
    }

    const goToPrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1)
        }
    }

    if (error) {
        return (
            <div
                style={{ padding: '20px', textAlign: 'center', color: '#666' }}
            >
                <p>{error}</p>
                <p>
                    <a href={src} target="_blank" rel="noopener noreferrer">
                        Open PDF in new tab
                    </a>
                </p>
            </div>
        )
    }

    return (
        <div
            ref={containerRef}
            style={{ position: 'relative', height: '100vh', overflow: 'auto' }}
        >
            {isLoading && (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1,
                    }}
                >
                    Loading PDF...
                </div>
            )}

            <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                <canvas ref={canvasRef} className={styles.pdfCanvas} />
            </div>

            {totalPages > 1 && (
                <div
                    style={{
                        textAlign: 'center',
                        padding: '10px',
                        borderTop: '1px solid #ccc',
                        backgroundColor: '#f5f5f5',
                    }}
                >
                    <button
                        onClick={goToPrevPage}
                        disabled={currentPage <= 1}
                        style={{ margin: '0 10px', padding: '5px 10px' }}
                    >
                        Previous
                    </button>
                    <span>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={goToNextPage}
                        disabled={currentPage >= totalPages}
                        style={{ margin: '0 10px', padding: '5px 10px' }}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    )
}

export const AnalysisPanel: React.FC<Props> = ({
    answer,
    activeTab,
    activeCitation,
    citationHeight,
    className,
    onActiveTabChanged,
    onToggleTab,
    selectedAnswer,
}) => {
    const isDisabledFeedbackTab: boolean = !answer.assistant_message_id
    const isDisabledCitationTab: boolean = !activeCitation
    const [content, setContent] = useState<CitationContent>({ kind: 'empty' })

    useEffect(() => {
        if (!activeCitation) {
            setContent({ kind: 'empty' })
            return
        }

        let cancelled = false
        let createdUrl: string | null = null
        const { path, hash } = parseCitation(activeCitation)

        ;(async () => {
            const response = await fetch(path, {
                method: 'GET',
                headers: await getHeaders(undefined),
            })

            if (CONTRIBUTION_CARD_PATTERN.test(path)) {
                const card = (await response.json()) as ContributionCard
                if (!cancelled) setContent({ kind: 'card', card })
                return
            }

            const blob = await response.blob()
            createdUrl = URL.createObjectURL(blob)
            const url = hash ? `${createdUrl}#${hash}` : createdUrl

            if (cancelled) {
                URL.revokeObjectURL(createdUrl)
                return
            }
            setContent({ kind: 'blob', url })
        })()

        return () => {
            cancelled = true
            if (createdUrl) URL.revokeObjectURL(createdUrl)
        }
    }, [activeCitation])

    const renderFileViewer = () => {
        if (content.kind === 'empty' || !activeCitation) {
            return null
        }

        if (content.kind === 'card') {
            const c = content.card
            const contributorName = c.is_anonymous
                ? 'Anonymous'
                : [c.contributor?.first_name, c.contributor?.last_name]
                      .filter(Boolean)
                      .join(' ') ||
                  c.contributor?.handle ||
                  'Unknown'
            return (
                <div
                    style={{
                        padding: '16px',
                        overflowY: 'auto',
                        height: '100%',
                    }}
                >
                    {c.question_text && (
                        <p style={{ fontWeight: 600, marginBottom: 8 }}>
                            {c.question_text}
                        </p>
                    )}
                    <p style={{ whiteSpace: 'pre-wrap', marginBottom: 12 }}>
                        {c.content}
                    </p>
                    <div style={{ fontSize: 12, color: '#666' }}>
                        <span>By {contributorName}</span>
                        {(c.tags?.length ?? 0) > 0 && (
                            <span style={{ marginLeft: 12 }}>
                                {c.tags.join(', ')}
                            </span>
                        )}
                    </div>
                </div>
            )
        }

        const { extension, pathWithoutHash } = parseCitation(activeCitation)

        if (extension === 'png') {
            return (
                <img
                    src={content.url}
                    className={styles.citationImg}
                    alt="Citation Image"
                />
            )
        }
        if (extension === 'md') {
            return <MarkdownViewer src={pathWithoutHash} />
        }
        // pathWithoutHash.includes('pdf') covers PDFs served by routes that
        // lack a .pdf suffix (e.g. /api/v1/documents/{id}/view).
        if (extension === 'pdf' || pathWithoutHash.includes('pdf')) {
            return <PDFViewer src={content.url} />
        }
        return (
            <iframe
                title="Citation"
                src={content.url}
                width="100%"
                height="100%"
            />
        )
    }

    const verticalGapStackTokens: IStackTokens = {
        childrenGap: 10,
        padding: 10,
    }

    const ratingStyle = (props: IRatingStyleProps): Partial<IRatingStyles> => ({
        root: {
            justifyContent: 'center',
            display: 'flex',
        },
        ratingStarBack: {
            color: '#edee76',
        },
        ratingStarFront: {
            color: '#edee76',
        },
        ratingButton: {
            '&:hover .ms-RatingStar-front': {
                color: '#dffbfd',
            },
            '&:hover .ms-RatingStar-back': {
                color: '#dffbfd',
            },
            '&:hover ~ .ms-Rating-button .ms-RatingStar-front': {
                color: '#edee76',
            },
            '&:hover ~ .ms-Rating-button .ms-RatingStar-back': {
                color: '#edee76',
            },
        },
    })

    const btnStyles = {
        root: {
            borderRadius: '0.3125em',
            backgroundColor: '#0a326b',
            border: '0px',
            color: 'white',
        },
        rootHovered: {
            backgroundColor: '#ca3fae',
            border: '0px',
            color: 'white',
        },
    }

    const stkStyles = {
        root: {
            color: '#0a326b',
            backgroundColor: 'white',
            marginLeft: '1.5rem',
            marginRight: '1.5rem',
            marginTop: '0.5rem',
            borderRadius: '16px',
            FontSizes: FontSizes.size16,
            FontWeights: FontWeights.semibold,
        },
    }

    const txtstyles = {
        subComponentStyles: {
            label: {
                root: {
                    color: '#0a326b',
                    FontSizes: FontSizes.size16,
                    fontFamily: 'Plus Jakarta Sans',
                    fontWeight: FontWeights.semibold,
                },
            },
        },
        fieldGroup: {
            borderRadius: '16px',
            width: '90%',
            height: '100px',
            margin: '20px auto',
            border: '1px solid grey',
        },
        field: {
            color: '#0a326b',
        },
    }

    const feedbackEntrty = () => {
        return (
            <Stack
                enableScopedSelectors
                tokens={verticalGapStackTokens}
                styles={stkStyles}
            >
                <p
                    style={{
                        color: '#0a326b',
                        fontSize: FontSizes.size16,
                        fontFamily: 'Plus Jakarta Sans',
                        fontWeight: FontWeights.semibold,
                        margin: '0.4rem',
                    }}
                >
                    Please provide feedback on response:
                </p>
                <Stack horizontal horizontalAlign="center">
                    <Rating
                        rating={starRating}
                        max={5}
                        size={RatingSize.Large}
                        onChange={onStarChange}
                        ariaLabel="Response rating"
                        ariaLabelFormat="{0} of {1} stars"
                        allowZeroStars
                        styles={ratingStyle}
                    />
                </Stack>
                <TextField
                    label="Any additional comments? (Optional)"
                    multiline
                    rows={3}
                    onChange={onCommentChange}
                    resizable={false}
                    borderless
                    styles={txtstyles}
                    placeholder="Type your feedback here..."
                />
                <Stack
                    horizontal
                    horizontalAlign="end"
                    styles={{ root: { paddingRight: 30 } }}
                >
                    <PrimaryButton onClick={makeApiRequest} styles={btnStyles}>
                        Submit
                    </PrimaryButton>
                </Stack>
            </Stack>
        )
    }

    const [starRating, setStarsRating] = useState(0)
    const [userComment, setUsersComment] = useState('null')

    const onStarChange = (
        event: React.FormEvent<HTMLElement>,
        rating?: number
    ) => {
        if (!rating) {
            setStarsRating(0)
        } else {
            setStarsRating(rating)
        }
    }

    const onCommentChange = (
        _ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        comment?: string
    ) => {
        if (!comment) {
            setUsersComment('null')
        } else {
            setUsersComment(comment)
        }
    }

    const makeApiRequest = async () => {
        if (!answer.assistant_message_id) {
            alert('Feedback is not available for this response yet.')
            return
        }
        if (starRating < 1) {
            alert('Please select a rating before submitting feedback.')
            return
        }

        const sanitizedUserComment = DOMPurify.sanitize(userComment, {
            FORBID_TAGS: [
                'style',
                'img',
                'src',
                'a',
                'h1',
                'p',
                'h2',
                'h3',
                'h4',
                'h5',
                'h6',
                'div',
                'script',
            ],
        })

        await ragFeedbackApi(
            answer.assistant_message_id,
            {
                rating: starRating,
                feedbackText:
                    sanitizedUserComment === 'null'
                        ? null
                        : sanitizedUserComment,
                feedbackType: 'helpfulness',
            },
            undefined
        )
        alert('Submitted a ' + starRating + ' rating.')
    }

    const handleClose = () => {
        if (activeTab !== undefined) {
            onToggleTab(activeTab, selectedAnswer)
        }
    }

    const showData = (): void => {
        alert(starRating)
    }

    const pvtStyles = {
        root: {
            padding: '2px',
            fontFamily: 'Plus Jakarta Sans',
            margin: '2px',
            fontSizes: FontSizes.size16,
        },
        link: {
            fontSize: FontSizes.size16,
            fontWeight: FontWeights.semibold,
            color: '#0a326b',
            fontFamily: 'Plus Jakarta Sans',
        },
        linkIsSelected: {
            color: '#0a326b',
            background: 'white',
            borderRadius: '6px',
            fontFamily: 'Plus Jakarta Sans',
            FontWeights: FontWeights.semibold,
            fontSizes: FontSizes.size16,
            selectors: {
                ':before': {
                    background: 'white',
                },
            },
        },
        // Target the tabpanel using selectors
        itemContainer: {
            selectors: {
                '& div[role="tabpanel"]': {
                    height: '100%',
                },
            },
        },
    }

    return (
        <div className={styles.analysisPanel}>
            <button className={styles.exitButton} onClick={handleClose}>
                <DismissFilled />
            </button>
            <Pivot
                className={className}
                selectedKey={activeTab}
                onLinkClick={(pivotItem) =>
                    pivotItem &&
                    onActiveTabChanged(
                        pivotItem.props.itemKey! as AnalysisPanelTabs
                    )
                }
                styles={pvtStyles}
            >
                <PivotItem
                    itemKey={AnalysisPanelTabs.FeedbackTab}
                    headerText="Feedback"
                    style={{ color: '#0a326b', fontSize: FontSizes.size16 }}
                    headerButtonProps={
                        isDisabledFeedbackTab
                            ? pivotItemDisabledStyle
                            : undefined
                    }
                >
                    {feedbackEntrty()}
                </PivotItem>
                <PivotItem
                    itemKey={AnalysisPanelTabs.CitationTab}
                    headerText="Citation"
                    headerButtonProps={
                        isDisabledCitationTab
                            ? pivotItemDisabledStyle
                            : undefined
                    }
                    className={styles.citationBox}
                >
                    {renderFileViewer()}
                </PivotItem>
            </Pivot>
        </div>
    )
}
